import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Building, 
  Save, 
  X, 
  MapPin, 
  Phone, 
  Mail, 
  Globe,
  Calendar,
  Users,
  FileText,
  AlertCircle
} from 'lucide-react';
import { facilityAPI } from '../../services/facilityApi';
import { 
  Facility, 
  CreateFacilityRequest, 
  FacilityType, 
  FacilityStatus 
} from '../../types/facility';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  padding: 24px 24px 0;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  color: #6b7280;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e5e7eb;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  
  &.required::after {
    content: ' *';
    color: #ef4444;
  }
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &.error {
    border-color: #ef4444;
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
  }
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &.error {
    border-color: #ef4444;
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &.error {
    border-color: #ef4444;
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CheckboxItem = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #374151;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  accent-color: #3b82f6;
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const SuccessMessage = styled.div`
  background: #d1fae5;
  color: #065f46;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const ModalFooter = styled.div`
  padding: 0 24px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          color: white;
          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
          }
          &:disabled {
            background: #9ca3af;
            cursor: not-allowed;
            transform: none;
            box-shadow: none;
          }
        `;
      default:
        return `
          background: white;
          color: #374151;
          border: 1px solid #d1d5db;
          &:hover {
            background: #f9fafb;
            border-color: #9ca3af;
          }
        `;
    }
  }}
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

interface FacilityFormProps {
  facility?: Facility;
  onClose: () => void;
  onSuccess: () => void;
}

const FacilityForm: React.FC<FacilityFormProps> = ({ facility, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<CreateFacilityRequest>({
    name: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'USA'
    },
    contact: {
      phone: '',
      email: '',
      website: ''
    },
    type: 'Hospital',
    status: 'Active',
    capacity: undefined,
    specialties: [],
    establishedDate: '',
    description: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const specialtyOptions = [
    'Cardiology',
    'Neurology',
    'Orthopedics',
    'Pediatrics',
    'Emergency Medicine',
    'Internal Medicine',
    'Surgery',
    'Oncology',
    'Radiology',
    'Pathology',
    'Anesthesiology',
    'Psychiatry',
    'Dermatology',
    'Ophthalmology',
    'ENT',
    'Urology',
    'Gynecology',
    'Geriatrics',
    'Rehabilitation',
    'Physical Therapy'
  ];

  useEffect(() => {
    if (facility) {
      setFormData({
        name: facility.name,
        address: facility.address,
        contact: facility.contact,
        type: facility.type,
        status: facility.status,
        capacity: facility.capacity,
        specialties: facility.specialties,
        establishedDate: facility.establishedDate,
        description: facility.description || ''
      });
    }
  }, [facility]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'Facility name is required';
    }

    if (!formData.address.street.trim()) {
      errors.street = 'Street address is required';
    }

    if (!formData.address.city.trim()) {
      errors.city = 'City is required';
    }

    if (!formData.address.state.trim()) {
      errors.state = 'State is required';
    }

    if (!formData.address.zipCode.trim()) {
      errors.zipCode = 'ZIP code is required';
    }

    if (!formData.contact.phone.trim()) {
      errors.phone = 'Phone number is required';
    }

    if (!formData.contact.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.establishedDate) {
      errors.establishedDate = 'Established date is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field: string, value: any) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      if (parent === 'address') {
        setFormData(prev => ({
          ...prev,
          address: {
            ...prev.address,
            [child]: value
          }
        }));
      } else if (parent === 'contact') {
        setFormData(prev => ({
          ...prev,
          contact: {
            ...prev.contact,
            [child]: value
          }
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSpecialtyChange = (specialty: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      specialties: checked 
        ? [...prev.specialties, specialty]
        : prev.specialties.filter(s => s !== specialty)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (facility) {
        await facilityAPI.updateFacility(facility.id, formData);
        setSuccess('Facility updated successfully!');
      } else {
        await facilityAPI.createFacility(formData);
        setSuccess('Facility created successfully!');
      }

      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    } catch (err) {
      setError(facility ? 'Failed to update facility' : 'Failed to create facility');
      console.error('Error saving facility:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <Building size={24} />
            {facility ? 'Edit Facility' : 'Add New Facility'}
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={24} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          {error && (
            <ErrorMessage>
              <AlertCircle size={20} />
              {error}
            </ErrorMessage>
          )}

          {success && (
            <SuccessMessage>
              <AlertCircle size={20} />
              {success}
            </SuccessMessage>
          )}

          <form onSubmit={handleSubmit}>
            <FormGrid>
              <FormSection>
                <SectionTitle>
                  <Building size={20} />
                  Basic Information
                </SectionTitle>

                <InputGroup>
                  <Label className="required">Facility Name</Label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className={validationErrors.name ? 'error' : ''}
                    placeholder="Enter facility name"
                  />
                  {validationErrors.name && (
                    <small style={{ color: '#ef4444' }}>{validationErrors.name}</small>
                  )}
                </InputGroup>

                <InputGroup>
                  <Label className="required">Facility Type</Label>
                  <Select
                    value={formData.type}
                    onChange={(e) => handleInputChange('type', e.target.value as FacilityType)}
                  >
                    <option value="Hospital">Hospital</option>
                    <option value="Clinic">Clinic</option>
                    <option value="Nursing Home">Nursing Home</option>
                    <option value="Rehabilitation Center">Rehabilitation Center</option>
                    <option value="Urgent Care">Urgent Care</option>
                    <option value="Specialty Center">Specialty Center</option>
                    <option value="Other">Other</option>
                  </Select>
                </InputGroup>

                <InputGroup>
                  <Label className="required">Status</Label>
                  <Select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value as FacilityStatus)}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Pending">Pending</option>
                    <option value="Suspended">Suspended</option>
                  </Select>
                </InputGroup>

                <InputGroup>
                  <Label>Capacity</Label>
                  <Input
                    type="number"
                    value={formData.capacity || ''}
                    onChange={(e) => handleInputChange('capacity', e.target.value ? parseInt(e.target.value) : undefined)}
                    placeholder="Enter facility capacity"
                    min="1"
                  />
                </InputGroup>

                <InputGroup>
                  <Label className="required">Established Date</Label>
                  <Input
                    type="date"
                    value={formData.establishedDate}
                    onChange={(e) => handleInputChange('establishedDate', e.target.value)}
                    className={validationErrors.establishedDate ? 'error' : ''}
                    max={new Date().toISOString().split('T')[0]}
                  />
                  {validationErrors.establishedDate && (
                    <small style={{ color: '#ef4444' }}>{validationErrors.establishedDate}</small>
                  )}
                </InputGroup>
              </FormSection>

              <FormSection>
                <SectionTitle>
                  <MapPin size={20} />
                  Address Information
                </SectionTitle>

                <InputGroup>
                  <Label className="required">Street Address</Label>
                  <Input
                    type="text"
                    value={formData.address.street}
                    onChange={(e) => handleInputChange('address.street', e.target.value)}
                    className={validationErrors.street ? 'error' : ''}
                    placeholder="Enter street address"
                  />
                  {validationErrors.street && (
                    <small style={{ color: '#ef4444' }}>{validationErrors.street}</small>
                  )}
                </InputGroup>

                <InputGroup>
                  <Label className="required">City</Label>
                  <Input
                    type="text"
                    value={formData.address.city}
                    onChange={(e) => handleInputChange('address.city', e.target.value)}
                    className={validationErrors.city ? 'error' : ''}
                    placeholder="Enter city"
                  />
                  {validationErrors.city && (
                    <small style={{ color: '#ef4444' }}>{validationErrors.city}</small>
                  )}
                </InputGroup>

                <InputGroup>
                  <Label className="required">State</Label>
                  <Input
                    type="text"
                    value={formData.address.state}
                    onChange={(e) => handleInputChange('address.state', e.target.value)}
                    className={validationErrors.state ? 'error' : ''}
                    placeholder="Enter state"
                  />
                  {validationErrors.state && (
                    <small style={{ color: '#ef4444' }}>{validationErrors.state}</small>
                  )}
                </InputGroup>

                <InputGroup>
                  <Label className="required">ZIP Code</Label>
                  <Input
                    type="text"
                    value={formData.address.zipCode}
                    onChange={(e) => handleInputChange('address.zipCode', e.target.value)}
                    className={validationErrors.zipCode ? 'error' : ''}
                    placeholder="Enter ZIP code"
                  />
                  {validationErrors.zipCode && (
                    <small style={{ color: '#ef4444' }}>{validationErrors.zipCode}</small>
                  )}
                </InputGroup>

                <InputGroup>
                  <Label>Country</Label>
                  <Input
                    type="text"
                    value={formData.address.country}
                    onChange={(e) => handleInputChange('address.country', e.target.value)}
                    placeholder="Enter country"
                  />
                </InputGroup>
              </FormSection>

              <FormSection>
                <SectionTitle>
                  <Phone size={20} />
                  Contact Information
                </SectionTitle>

                <InputGroup>
                  <Label className="required">Phone Number</Label>
                  <Input
                    type="tel"
                    value={formData.contact.phone}
                    onChange={(e) => handleInputChange('contact.phone', e.target.value)}
                    className={validationErrors.phone ? 'error' : ''}
                    placeholder="Enter phone number"
                  />
                  {validationErrors.phone && (
                    <small style={{ color: '#ef4444' }}>{validationErrors.phone}</small>
                  )}
                </InputGroup>

                <InputGroup>
                  <Label className="required">Email Address</Label>
                  <Input
                    type="email"
                    value={formData.contact.email}
                    onChange={(e) => handleInputChange('contact.email', e.target.value)}
                    className={validationErrors.email ? 'error' : ''}
                    placeholder="Enter email address"
                  />
                  {validationErrors.email && (
                    <small style={{ color: '#ef4444' }}>{validationErrors.email}</small>
                  )}
                </InputGroup>

                <InputGroup>
                  <Label>Website</Label>
                  <Input
                    type="url"
                    value={formData.contact.website || ''}
                    onChange={(e) => handleInputChange('contact.website', e.target.value)}
                    placeholder="Enter website URL"
                  />
                </InputGroup>
              </FormSection>

              <FormSection>
                <SectionTitle>
                  <FileText size={20} />
                  Additional Information
                </SectionTitle>

                <InputGroup>
                  <Label>Specialties</Label>
                  <CheckboxGroup>
                    {specialtyOptions.map((specialty) => (
                      <CheckboxItem key={specialty}>
                        <Checkbox
                          type="checkbox"
                          checked={formData.specialties.includes(specialty)}
                          onChange={(e) => handleSpecialtyChange(specialty, e.target.checked)}
                        />
                        {specialty}
                      </CheckboxItem>
                    ))}
                  </CheckboxGroup>
                </InputGroup>

                <InputGroup>
                  <Label>Description</Label>
                  <TextArea
                    value={formData.description || ''}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Enter facility description"
                  />
                </InputGroup>
              </FormSection>
            </FormGrid>
          </form>
        </ModalBody>

        <ModalFooter>
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="primary" 
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <LoadingSpinner />
            ) : (
              <Save size={20} />
            )}
            {loading ? 'Saving...' : (facility ? 'Update Facility' : 'Create Facility')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

export default FacilityForm;
